import React, { useContext, useEffect, useState } from "react";
import PropTypes from "prop-types";
import {
    useTable,
    useSortBy,
    useFilters,
    usePagination,
    useExpanded,
} from "react-table";
import { useUID } from "react-uid";

import { usePrevious } from "shared/hooks/usePrevious";
import { COLOR_VALUES } from "shared/config/colors";
import BodyCell from "shared/components/data-display/tables/BodyCell";
import HeadCell from "shared/components/data-display/tables/HeadCell";
import EmptyState from "shared/components/data-display/tables/EmptyState";
import Pager, {
    PAGER_INITIAL_PAGE_SIZE,
} from "shared/components/data-display/tables/Pager";

import { ServerSidePaginationContext } from "shared/contexts/ServerSidePaginationContext";
import {
    StyledTable,
    StyledLoaderTr,
} from "./Table.styles";

const Table = ({
    dataIsFetching = false,
    columns,
    fixedHeadingRows = [],
    data,
    getRowProps = () => void 0,
    grayBgHeader = false,
    filters = [],
    emptyMessage,
    emptyMessageTableTitle,
    emptyActionProps,
    showTableBorder = true,
    paginationFromFE = true,
    paginationAfterTotalRowCount = 10,
    pagerDisabledOptionsList = [],
    sortBy = [],
    pageSizeInitially = PAGER_INITIAL_PAGE_SIZE,
    displayPager = true,
    expandedAllRows = false,
    noContentInHeaderRow = false,
}) => {
    const uniqId = useUID();

    const {
        querySortBy,
        queryPageIndex,
        queryPageSize,
        totalItems,
        queryPageSizeUpdate,
        queryPageIndexUpdate,
    } = useContext(ServerSidePaginationContext);

    const [values, setValues] = useState({
        totalCount: paginationFromFE ? data.length : totalItems,
        displayPagination: paginationFromFE
            ? data?.length > paginationAfterTotalRowCount
            : totalItems > paginationAfterTotalRowCount,
    });

    useEffect(() => {
        if (!paginationFromFE) {
            setValues(v => ({
                ...v,
                displayPagination: totalItems > paginationAfterTotalRowCount,
            }));
        }
    }, [totalItems, paginationFromFE, paginationAfterTotalRowCount]);

    const tableInstance = useTable(
        {
            columns,
            data,
            hiddenColumns: columns
                .filter((column) => !column?.show)
                .map((column) => column.id),
            initialState: {
                filters: filters,
                pageIndex: paginationFromFE ? 0 : queryPageIndex,
                pageSize: paginationFromFE ? pageSizeInitially : queryPageSize,
                sortBy: sortBy,
                expanded: expandedAllRows,
            },
            pageCount: Math.ceil(totalItems / queryPageSize),
            manualPagination: !paginationFromFE,
            manualSortBy: !paginationFromFE,
            autoResetPage: true,
            autoResetSortBy: false,
            autoResetFilters: false,
        },
        useFilters,
        useSortBy,
        useExpanded,
        usePagination
    );

    const {
        getTableProps,
        getTableBodyProps,
        headerGroups,
        rows,
        page,
        prepareRow,
        setHiddenColumns,
        setAllFilters,
        canPreviousPage,
        canNextPage,
        pageOptions,
        gotoPage,
        nextPage,
        previousPage,
        setPageSize,
        toggleAllRowsExpanded,
        isAllRowsExpanded,    
        state: { pageIndex, pageSize, expanded  },
    } = tableInstance;
    const tableRows = values.displayPagination ? page : rows;

    useEffect(() => {
        setHiddenColumns(
            columns
                .filter((column) =>
                    column.hasOwnProperty("show") ? !column?.show : null
                )
                .map((column) => column?.accessor)
        );
    }, [columns, setHiddenColumns]);

    useEffect(() => {
        if (expanded) toggleAllRowsExpanded(true);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [isAllRowsExpanded]);

    // for pagination from frontend
    useEffect(() => {
        if (paginationFromFE) {
            setValues(v => ({
                ...v,
                totalCount: rows?.length,
                displayPagination: rows?.length > paginationAfterTotalRowCount,
                expandedAllRows,
            }));
            !expandedAllRows && toggleAllRowsExpanded(false);
        }
    }, [rows.length, page.length, pageIndex, pageSize, paginationFromFE, paginationAfterTotalRowCount, expandedAllRows, toggleAllRowsExpanded]);

    // for pagination from backend
    useEffect(() => {
        if (!paginationFromFE) {
            queryPageIndexUpdate(pageIndex);
        }
    }, [pageIndex, paginationFromFE, queryPageIndexUpdate]);

    useEffect(() => {
        if (!paginationFromFE) {
            queryPageSizeUpdate(pageSize);
            queryPageIndexUpdate(0);
            gotoPage(0);
        }
    }, [pageSize, gotoPage, paginationFromFE, queryPageIndexUpdate, queryPageSizeUpdate]);

    const prevFilters = usePrevious(filters);

    useEffect(() => {
        JSON.stringify(filters) !== JSON.stringify(prevFilters) &&
            setAllFilters(filters);
    }, [filters, prevFilters, setAllFilters]);

    const getCurrentSort = (isSortedDesc) => {
        if (isSortedDesc === true) {
            return "desc";
        } else if (isSortedDesc === false) {
            return "asc";
        } else {
            return "none";
        }
    };

    const getBESortVal = (isSortedDesc, entityName) => {
        if (isSortedDesc === true) {
            return `${entityName} DESC`;
        } else if (isSortedDesc === false) {
            return `${entityName} ASC`;
        } else {
            return "";
        }
    };

    if (!paginationFromFE && !tableRows.length && data?.length > 0) {
        return <>Loading...</>;
    }

    if (tableRows.length) {
        return (
            <>
                <StyledTable
                    showTableBorder={showTableBorder}
                    {...getTableProps()}
                >
                    <thead style={{ position: "relative" }}>
                        {headerGroups.map((headerGroup) => (
                            <tr {...headerGroup.getHeaderGroupProps()}>
                                {headerGroup.headers.map((column) => {
                                    const currentSort = getCurrentSort(
                                        column.isSortedDesc
                                    );
                                    const sortValParamBE = paginationFromFE
                                        ? undefined
                                        : getBESortVal(
                                            column.isSortedDesc,
                                            column.id
                                        );
                                    return (
                                        <HeadCell
                                            {...column.headCellProps}
                                            sortable={column.canSort}
                                            currentSort={currentSort}
                                            sortByBEVal={sortValParamBE}
                                            loadingIcon={
                                                sortValParamBE
                                                    ? dataIsFetching &&
                                                    querySortBy ===
                                                    sortValParamBE
                                                    : false
                                            }
                                            noContentInHeaderRow={noContentInHeaderRow}
                                            grayBgHeader={grayBgHeader}
                                            {...column.getHeaderProps(
                                                column.getSortByToggleProps()
                                            )}
                                        />
                                    );
                                })}
                            </tr>
                        ))}
                        {dataIsFetching && <StyledLoaderTr />}

                        {/* external fixed row data of respective columns it's not a part of pager/useTable */}
                        {fixedHeadingRows.map((rowData, rowDataIndex) => (
                            <tr key={`rowIndex${rowDataIndex + uniqId}`}>
                                {rowData.data.map((cellData, cellDataIndex) => (
                                    <HeadCell                                    
                                        key={`cellIndex${cellDataIndex + uniqId
                                            }`}
                                        borderBottom={rowData.borderBottom}
                                        text={cellData.text}
                                        alignedRight={cellData.alignedRight}
                                        bgColor={
                                            rowData.bgColor ??
                                            COLOR_VALUES.Gray_7
                                        }
                                        bgTextColor={cellData.textColor ?? COLOR_VALUES.Black}
                                        variant={rowData.variant}
                                    />
                                ))}
                            </tr>
                        ))}
                    </thead>
                    <tbody {...getTableBodyProps()}>
                        {tableRows.map((row) => {
                            prepareRow(row);

                            return (
                                <tr {...row.getRowProps(getRowProps(row))}>
                                    {row.cells.map((cell) => (
                                        <BodyCell
                                            {...cell.getCellProps({
                                                ...cell.column.getBodyCellProps(
                                                    cell
                                                ),
                                                compact: tableRows.length > 24,
                                                expanded: cell.row.isExpanded,                                                
                                            })}
                                        />
                                    ))}
                                </tr>
                            );
                        })}
                    </tbody>
                </StyledTable>

                {displayPager && values.displayPagination && (
                    <Pager
                        totalEntries={
                            paginationFromFE ? values.totalCount : totalItems
                        }
                        pagerDisabledOptionsList={pagerDisabledOptionsList}
                        {...{
                            canPreviousPage,
                            canNextPage,
                            pageOptions,
                            gotoPage,
                            nextPage,
                            previousPage,
                            setPageSize,
                            state: { pageIndex, pageSize },
                        }}
                    />
                )}
            </>
        );
    } else {
        return (
            <EmptyState
                message={emptyMessage}
                title={emptyMessageTableTitle}
                actionProps={emptyActionProps}
            />
        );
    }
};

Table.propTypes = {
    dataIsFetching: PropTypes.bool,
    columns: PropTypes.array.isRequired,
    fixedHeadingRows: PropTypes.array,
    data: PropTypes.array.isRequired,
    getRowProps: PropTypes.func,
    grayBgHeader: PropTypes.bool,
    filters: PropTypes.array,
    emptyMessage: PropTypes.string,
    emptyMessageTableTitle: PropTypes.string,
    emptyActionProps: PropTypes.object,
    showTableBorder: PropTypes.bool,
    paginationFromFE: PropTypes.bool,
    paginationAfterTotalRowCount: PropTypes.number,
    pagerDisabledOptionsList: PropTypes.array,
    sortBy: PropTypes.array,
    pageSizeInitially: PropTypes.number,
    displayPager: PropTypes.bool,
    expandedAllRows: PropTypes.bool,
    noContentInHeaderRow: PropTypes.bool,
};

export default Table;
